import React from 'react';
import { useApp } from '../context/AppContext';

const languages = [
  { code: 'nl', name: 'Nederlands', flag: '🇳🇱', native: 'Nederlands' },
  { code: 'en', name: 'English', flag: '🇬🇧', native: 'English' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', native: 'Français' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹', native: 'Italiano' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', native: 'Deutsch' },
  { code: 'es', name: 'Español', flag: '🇪🇸', native: 'Español' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦', native: 'العربية', rtl: true },
  { code: 'pl', name: 'Polski', flag: '🇵🇱', native: 'Polski' },
  { code: 'sv', name: 'Svenska', flag: '🇸🇪', native: 'Svenska' },
];

const LanguageSelector = () => {
  const { setLanguage } = useApp();

  const handleSelectLanguage = (code) => {
    // Set RTL direction for Arabic
    const selectedLang = languages.find(l => l.code === code);
    document.documentElement.dir = selectedLang?.rtl ? 'rtl' : 'ltr';
    setLanguage(code);
  };

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center px-6 relative grain"
      style={{ background: 'radial-gradient(circle at center, #2C1B18 0%, #050505 100%)' }}
      data-testid="language-selector"
    >
      {/* Background Image Overlay */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage: 'url(https://images.unsplash.com/photo-1757313239816-9bac11170af8?w=1920)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      />
      
      <div className="relative z-10 max-w-2xl w-full">
        {/* Logo with shimmer */}
        <div className="text-center mb-12 animate-fade-in">
          <h1 className="font-heading text-4xl md:text-5xl tracking-tight mb-3">
            <span className="zuiver-shimmer-static">Zuiver</span>
            <span className="text-white">Parfum</span>
          </h1>
          <p className="font-accent text-xs tracking-[0.3em] text-[#A8A29E] uppercase">
            Luxe Geuren
          </p>
        </div>

        {/* Select Language Text */}
        <h2 className="font-heading text-2xl text-center text-white mb-8 animate-fade-in opacity-0 stagger-1">
          Select Your Language
        </h2>

        {/* Language Grid - 3 columns for 9 languages */}
        <div className="grid grid-cols-3 gap-3 md:gap-4">
          {languages.map((lang, index) => (
            <button
              key={lang.code}
              onClick={() => handleSelectLanguage(lang.code)}
              className={`
                glass p-4 md:p-5 rounded-xl
                flex flex-col items-center justify-center
                hover:border-[#D4AF37]/50 hover:bg-white/5
                transition-all duration-300
                animate-fade-in opacity-0
                group
              `}
              style={{ animationDelay: `${(index + 2) * 80}ms` }}
              data-testid={`language-btn-${lang.code}`}
            >
              <span className="text-2xl md:text-3xl mb-2">{lang.flag}</span>
              <span className={`text-white text-sm md:text-base font-medium group-hover:text-[#D4AF37] transition-colors ${lang.rtl ? 'font-arabic' : ''}`}>
                {lang.native}
              </span>
            </button>
          ))}
        </div>

        {/* Footer Text */}
        <p className="text-center text-[#57534E] text-sm mt-12 animate-fade-in opacity-0 stagger-7">
          You can change the language at any time in settings
        </p>
      </div>

      {/* Shimmer CSS */}
      <style>{`
        .zuiver-shimmer-static {
          background: linear-gradient(
            90deg, 
            #D4AF37 0%, 
            #D4AF37 40%, 
            #FFD700 50%, 
            #D4AF37 60%, 
            #D4AF37 100%
          );
          background-size: 200% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: shimmerText 6s ease-in-out infinite;
        }
        
        @keyframes shimmerText {
          0%, 80% {
            background-position: 200% center;
          }
          90%, 100% {
            background-position: -200% center;
          }
        }
      `}</style>
    </div>
  );
};

export default LanguageSelector;
